import { useEffect, useMemo, useState } from 'react';
import type { SVGProps, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, message } from 'antd';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  PlusIcon,
  SearchIcon,
  CaretIcon,
  ClearFilterIcon,
  CrLocationIcon,
  AssignmentIcon,
  CatRoadsIcon,
  CatWaterIcon,
  CatPensionIcon,
  CatEducationIcon,
  CrEditIcon,
  CrDeleteIcon,
} from '../../assets/icons';
import {
  useGetCitizenRequestsQuery,
  useDeleteCitizenRequestMutation,
  crCode,
  crName,
  crLocation,
  crCategory,
} from '../../features/citizenRequests/citizenRequestApi';

/** Category → column glyph. Falls back to a generic doc icon. */
const CATEGORY_ICON: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  Roads: CatRoadsIcon,
  'Water Supply': CatWaterIcon,
  Pension: CatPensionIcon,
  Education: CatEducationIcon,
};

const AVATAR_TINTS = [
  'bg-[#a0baff] text-[#2e4986]',
  'bg-[#cf4b00] text-white',
  'bg-[#eef4ff] text-[#424655]',
  'bg-[#dbeafe] text-[#0061ff]',
];

/** Classify a free-form status into the design's pill colors. */
function statusPill(raw: string) {
  const s = (raw || '').toLowerCase();
  if (s.includes('progress')) return { label: raw || 'In Progress', cls: 'bg-[#0d6efd] text-white' };
  if (s.includes('close') || s.includes('resolved') || s.includes('done') || s.includes('dispos'))
    return { label: raw || 'Closed', cls: 'bg-[#eef4ff] text-[#424655]' };
  return { label: raw || 'Open', cls: 'bg-[#a0baff] text-[#2e4986]' };
}

/** Filter label → the API query param it maps to (Location/Assigned To have no server filter yet). */
const FILTERS: { label: string; param?: 'status' | 'issue_type' | 'priority'; options: string[] }[] = [
  { label: 'Status', param: 'status', options: ['All', 'Open', 'In Progress', 'Closed'] },
  { label: 'Category', param: 'issue_type', options: ['All', 'Roads', 'Water Supply', 'Pension', 'Education'] },
  { label: 'Priority', param: 'priority', options: ['All', 'Critical', 'High', 'Medium', 'Low'] },
  { label: 'Location', options: ['All'] },
  { label: 'Assigned To', options: ['All'] },
];

const GRID =
  'grid grid-cols-[120px_minmax(0,1.3fr)_150px_170px_120px_130px_90px] items-center';
const TH = 'text-[11px] font-semibold uppercase tracking-[0.4px] text-[#424655]/70';

function initials(name: string) {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '—';
}

export default function CitizenRequestsPage() {
  const navigate = useNavigate();
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>(
    Object.fromEntries(FILTERS.map((f) => [f.label, 'All'])),
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setQuery(queryInput.trim()), 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  // Reset to page 1 whenever the query/filters change.
  useEffect(() => setPage(1), [query, filters]);

  const { data, isFetching } = useGetCitizenRequestsQuery({
    ...(query ? { search: query } : {}),
    ...(filters.Status !== 'All' ? { status: filters.Status } : {}),
    ...(filters.Category !== 'All' ? { issue_type: filters.Category } : {}),
    ...(filters.Priority !== 'All' ? { priority: filters.Priority } : {}),
    page,
    pageSize,
  });
  const [deleteRequest, { isLoading: deleting }] = useDeleteCitizenRequestMutation();
  const [pendingDelete, setPendingDelete] = useState<{ id: number; code: string } | null>(null);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  const setFilter = (label: string, value: string) =>
    setFilters((prev) => ({ ...prev, [label]: value }));
  const clearFilters = () => {
    setQueryInput('');
    setQuery('');
    setFilters(Object.fromEntries(FILTERS.map((f) => [f.label, 'All'])));
  };
  const filtersActive = query !== '' || Object.values(filters).some((v) => v !== 'All');

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRequest(pendingDelete.id).unwrap();
      message.success(`${pendingDelete.code} deleted.`);
      setPendingDelete(null);
    } catch {
      message.error('Could not delete the request. Please try again.');
    }
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 rounded-xl bg-white px-8 py-5 shadow-card lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-9 text-[#00173e]">Citizen Requests</h1>
          <p className="text-sm text-ink-secondary">
            Manage and track all incoming requests from citizens.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/user/citizen-requests/new')}
          className="flex h-[38px] shrink-0 items-center justify-center gap-2 rounded-full bg-brand px-6 text-xs font-bold tracking-[0.6px] text-white shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-colors hover:bg-brand-dark"
        >
          <PlusIcon className="size-[10.5px]" />
          Add Citizen Request
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-[18px] rounded-xl bg-white p-6 shadow-card">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 size-[18px] -translate-y-1/2 text-[#727787]" />
          <input
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Search by CR ID, citizen name, phone, location..."
            className="w-full rounded-lg bg-[#eef4ff] py-3.5 pl-12 pr-4 text-base text-ink shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] placeholder:text-[#727787] focus:outline-none focus:ring-2 focus:ring-brand/15"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {FILTERS.map((f) => (
            <div key={f.label} className="cr-filter">
              <Select
                variant="borderless"
                value={filters[f.label]}
                onChange={(v) => setFilter(f.label, v)}
                labelRender={(o) => `${f.label}: ${o.label ?? o.value}`}
                options={f.options.map((o) => ({ value: o, label: o }))}
                popupMatchSelectWidth={false}
                suffixIcon={null}
                className="cr-filter-select"
              />
              <CaretIcon className="pointer-events-none absolute right-4 top-1/2 size-2.5 -translate-y-1/2 text-[#424655]" />
            </div>
          ))}
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-auto flex items-center gap-1 text-xs font-bold tracking-[0.6px] text-[#0057cd] hover:underline"
            >
              <ClearFilterIcon className="size-3.5" />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#f1f5f9] bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[980px]">
            {/* Header */}
            <div className={[GRID, 'border-b border-[#eef4ff] bg-[#eef4ff] px-6 py-3'].join(' ')}>
              <span className={['flex items-center gap-1', TH].join(' ')}>
                CR ID
                <span className="flex flex-col text-[#cbd5e1]">
                  <CaretIcon className="size-2.5 -rotate-180" />
                  <CaretIcon className="-mt-1 size-2.5" />
                </span>
              </span>
              <span className={TH}>Citizen</span>
              <span className={TH}>Location</span>
              <span className={TH}>Category</span>
              <span className={TH}>Tasks</span>
              <span className={TH}>Status</span>
              <span className={[TH, 'text-right'].join(' ')}>Actions</span>
            </div>

            {/* Rows */}
            {rows.map((c, i) => {
              const status = statusPill(c.status || '');
              const code = crCode(c);
              const category = crCategory(c);
              const CatIcon = CATEGORY_ICON[category] ?? AssignmentIcon;
              const tasks = c.tasks_count ?? 0;
              return (
                <div
                  key={c.id}
                  className={[GRID, 'px-6 py-4 transition-colors hover:bg-[#f8fafc]', i > 0 ? 'border-t border-[#eef4ff]' : ''].join(' ')}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/user/citizen-requests/${c.id}`)}
                    className="text-left font-mono text-[13px] font-semibold text-ink hover:text-brand hover:underline"
                  >
                    {code}
                  </button>
                  <div className="flex items-center gap-2.5 pr-3">
                    <span className={['flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold', AVATAR_TINTS[i % AVATAR_TINTS.length]].join(' ')}>
                      {initials(crName(c))}
                    </span>
                    <span className="truncate text-sm font-medium text-[#334155]">{crName(c)}</span>
                  </div>
                  <span className="flex items-center gap-1.5 text-sm text-[#424655]">
                    <CrLocationIcon className="size-3.5 shrink-0 text-[#727787]" />
                    <span className="truncate">{crLocation(c) || '—'}</span>
                  </span>
                  <div>
                    {category ? (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-[#eef4ff] px-2.5 py-1 text-xs font-medium text-[#424655]">
                        <CatIcon className="size-3.5 text-[#424655]" />
                        {category}
                      </span>
                    ) : (
                      <span className="text-sm text-ink-secondary">—</span>
                    )}
                  </div>
                  <div className="flex items-center">
                    {tasks > 0 ? (
                      Array.from({ length: Math.min(tasks, 4) }, (_, t) => (
                        <span
                          key={t}
                          className="-ml-2 flex size-6 items-center justify-center rounded-full border-2 border-white bg-[#e6e7f3] text-[10px] font-bold text-[#424655] first:ml-0"
                        >
                          {t + 1}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-ink-secondary">—</span>
                    )}
                  </div>
                  <div>
                    <span className={['inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold', status.cls].join(' ')}>
                      {status.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      aria-label="Edit request"
                      onClick={() => navigate(`/user/citizen-requests/${c.id}/edit`)}
                      className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-brand-shade hover:text-brand"
                    >
                      <CrEditIcon className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete request"
                      onClick={() => setPendingDelete({ id: c.id, code })}
                      className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#fee2e2] hover:text-[#dc2626]"
                    >
                      <CrDeleteIcon className="size-4" />
                    </button>
                  </div>
                </div>
              );
            })}

            {rows.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">
                {isFetching ? 'Loading requests…' : 'No citizen requests found.'}
              </div>
            )}

            {/* Footer */}
            <Pagination
              rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
              rangeEnd={Math.min(page * pageSize, total)}
              total={total}
              noun="results"
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(v) => {
                setPageSize(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        open={!!pendingDelete}
        title="Delete Citizen Request"
        message={
          <>
            Are you sure you want to delete <strong>{pendingDelete?.code}</strong>? This action
            cannot be undone.
          </>
        }
        loading={deleting}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
